import React from 'react';
import { motion } from 'framer-motion';
import ProjectStatsBadge from '../../../components/ProjectStatsBadge';
import ShareProjectButton from '../../../components/ShareProjectButton/ShareProjectButton';
import './ops-mind-ai.css';
import opsMindAIImg from '../../../assets/images/OpsMind AI.png';
import qrCodeImg from '../../../assets/images/OpsMind-AI.png';

const OpsMindAI = () => {
  return (
    <motion.div 
      className="project-detail-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.h1 
        className="project-heading"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        OpsMind <span>AI</span> – Context-Aware Corporate Knowledge Assistant
      </motion.h1>

      <motion.p 
        className="last-updated"
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <br />
        Last Updated On: 8th March 2026
      </motion.p>

      <div className="project-content">
        <motion.div 
          className="project-left"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <img src={opsMindAIImg} alt="OpsMind AI" className="project-image" />

          <h2 className="project-subtitle">OpsMind AI</h2>
          <span className="project-category">Software Applications</span>
          <p className="project-author">Author: Praveen Kumar B</p>

          <ProjectStatsBadge docId="opsMindAI" trackView={true} />

          {/* Buttons */}
          <div className="project-buttons">
            <a
              href="https://ops-mind-ai-frontend.vercel.app/"
              className="visit-project-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              → Visit the Project
            </a>

            <a
              href="https://github.com/Praveenkumar-king"
              className="visit-project-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              → View on GitHub
            </a>

            <ShareProjectButton
              project={{
                id: "opsMindAI",
                title: "OpsMind AI",
                path: "/ops-mind-ai"
              }}
            />
          </div>

           <div className="qr-container">
            <img src={qrCodeImg} alt="QR Code" className="qr-image" />
          </div>
          
        </motion.div>

        <motion.div 
          className="project-right"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3 className="section-title">Project Details</h3>

          <p className="overview-text">
            Hi Friends 👋, Here’s a complete overview of the <strong>OpsMind AI</strong>.
          </p>

          <h3 className="section-title">Overview ✨</h3>

          <p className="overview-description">
            OpsMind AI is a context-aware corporate knowledge assistant designed to help employees quickly find reliable information from internal company documents. It uses Retrieval-Augmented Generation (RAG), semantic search, and MongoDB Atlas Vector Search to retrieve relevant document content before generating an answer with Groq. Every grounded response can be supported with verifiable document and page-level citations, while a hallucination guardrail responds with “I don’t know” when sufficient information cannot be found in the indexed knowledge base.
          </p>

          <h3 className="section-title">Key Features 🛠️</h3>

          <ul className="feature-list">
            <li>AI Knowledge Assistant — Ask natural-language questions about company policies, SOPs, HR guidelines, and internal documents.</li>
            <li>RAG-Based Question Answering — Retrieves relevant document content before generating responses.</li>
            <li>PDF Knowledge Ingestion — Upload and process corporate PDF documents.</li>
            <li>Semantic Search — Finds contextually relevant information instead of relying only on keyword matching.</li>
            <li>Verified Citations — Provides document names, page numbers, and relevant source excerpts.</li>
            <li>Hallucination Guardrail — Avoids unsupported answers when relevant information is unavailable.</li>
            <li>Conversation History — Saves and manages previous AI conversations.</li>
            <li>Role-Based Authentication — Separate secure User and Admin experiences.</li>
          </ul>

          <h3 className="section-title">Advanced Features 🚀</h3>

          <ul className="feature-list">
            <li>MongoDB Atlas Vector Search for high-quality semantic retrieval.</li>
            <li>Document Chunking & Embeddings for efficient knowledge indexing.</li>
            <li>Groq-Powered AI Generation for fast response generation.</li>
            <li>Real-Time AI Response Streaming for a ChatGPT-like conversational experience.</li>
            <li>Cloudinary Document Storage for secure centralized PDF storage.</li>
            <li>Admin RAG Playground for testing retrieval and AI responses.</li>
            <li>Knowledge Analytics for monitoring queries, sources, and knowledge gaps.</li>
            <li>Knowledge Graph for visualizing relationships within the knowledge base.</li>
            <li>Hallucination & Grounding Monitoring to evaluate answer reliability.</li>
            <li>User Management & Audit Logs for enterprise administration and activity tracking.</li>
            <li>Secure Password Recovery with Brevo email delivery and time-limited reset links.</li>
          </ul>

          <h3 className="section-title">Why OpsMind AI? 💡</h3>

          <p className="overview-description">
            Traditional corporate knowledge systems often require employees to manually search through large collections of documents and folders. OpsMind AI transforms this process into an intelligent conversational experience.
          </p>

          <p className="overview-description">
            Instead of simply generating answers from general AI knowledge, OpsMind AI first retrieves relevant information from the organization's indexed documents and uses that context to generate grounded responses. Its citation engine improves transparency and verifiability, while the hallucination guardrail helps prevent unsupported answers.
          </p>

          <p className="overview-description">
            This makes OpsMind AI suitable for organizations that need faster knowledge discovery, reliable document-based answers, and controlled AI-assisted access to internal information.
          </p>

          <h3 className="section-title">Conclusion 🎯</h3>

          <p className="overview-description">
            OpsMind AI combines RAG, vector search, LLM generation, document intelligence, and enterprise administration into a single corporate knowledge platform. It demonstrates how modern AI can transform scattered organizational documents into an interactive, searchable, and citation-backed knowledge system while keeping answer reliability and source verification at the center.
          </p>

        </motion.div>
      </div>
    </motion.div>
  );
};

export default OpsMindAI;